import { CompassOutlined, ReloadOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tabs,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import type { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { CurrentUser } from "../../api/currentUser";
import { ApiProblem, createRequestId } from "../../api/httpClient";
import { useApiResource } from "../../api/useApiResource";
import { useActorContext } from "../../auth/ActorContext";
import { can } from "../../auth/effectivePermissions";
import { label } from "../../lib/labels";
import { useCatalog } from "../catalogs/useCatalog";
import { EmptyState } from "../../components/EmptyState";
import { ErrorState } from "../../components/ErrorState";
import { ForbiddenState } from "../../components/ForbiddenState";
import { LoadingSkeleton } from "../../components/LoadingSkeleton";
import { PageHeading } from "../../components/PageHeading";
import { StaleState } from "../../components/StaleState";
import { StatusBadge } from "../../components/StatusBadge";
import { formatDateVn, formatDateTimeVn } from "../../lib/dateFormat";
import {
  getCemetery,
  getCemeteryManagementHistory,
  updateCemetery,
  type CemeteryData,
  type CemeteryManagementHistoryEntryData,
  type UpdateCemeteryInput,
} from "./cemeteryApi";
import { IdentityStrip } from "./IdentityStrip";
import { formatCount } from "./cemeteryFormat";
import { GisMapPage } from "./gis/GisMapPage";
import "./cemeteries.css";

/**
 * SCR-033 — Hồ sơ nghĩa trang (S11.6, mockup D01; relayout 14/09), persona ACT06 với nhiệm vụ
 * "xem hồ sơ và cập nhật đúng phần được phép". Bố cục (layout A):
 *
 * - **Header band**: tên + action làm mới; dòng meta một hàng mang mã, badge trạng thái và địa
 *   chỉ (địa chỉ CHỈ xuất hiện ở đây, rail không lặp lại).
 * - **Dải danh tính**: điểm nhấn duy nhất của màn — bar tỷ lệ 100% đã xác định / một phần /
 *   chưa xác định kèm nhãn chữ đủ ba vế.
 * - **Hai cột**: cột chính là sơ đồ ô thửa khu — lô — hàng MỞ SẴN (không còn cây bấm-xổ) hoặc
 *   chỉ mục khu khi nghĩa trang vượt ngưỡng quy mô; rail phải sticky giữ thông tin quản lý và
 *   thống kê bên cạnh khi cuộn sơ đồ dài.
 * - **Progressive**: nhóm tài liệu/ảnh, bản đồ/công trình và lịch sử quản lý. Lịch sử là tab
 *   CUỐI và chỉ tải khi được mở — màn chi tiết không phát sinh request phụ nào ngoài bản ghi
 *   chính (yêu cầu "zero requests until opened" của thiết kế khóa).
 * - **Hành động cập nhật**: "Cập nhật hồ sơ" nằm ở ĐẦU trang, ngang hàng "Làm mới", chỉ render
 *   khi grant hiện hành có `DATA_WRITE` đúng scope bản ghi. Trước đây nó bị tách xuống một khối
 *   riêng cuối trang với lý do "hành động hiếm" (Spec SCR-033 §4.6); xếp lại 17/09/2026 vì
 *   guardrail IRM §5.6 định nghĩa hành động hiếm là *sáp nhập / chuyển quản lý / đóng* — sửa
 *   tên-địa chỉ-diện tích-mô tả là sửa thường, không thuộc nhóm đó. Xem DEC-059.
 *   Modal `UpdateCemeteryModal` giữ nguyên hợp đồng (allowlist field, `expectedVersion` gửi ngầm).
 *
 * Negative disclosure: `cemeteryId`/`sectionId` là UUID kỹ thuật (NEVER_USER_UI) — chúng đi
 * theo href vận chuyển như mọi link danh sách→chi tiết của app, nhưng KHÔNG bao giờ xuất hiện
 * dưới dạng nội dung đọc được; `version` là optimistic-lock metadata, `graveStats`/
 * `sectionCount` là tên trường API. Màn KHÔNG có editor hình học, ranh giới, centroid hay
 * control "Cập nhật hiện trạng" mơ hồ (Spec §5).
 */

const HISTORY_PAGE_SIZE = 10;

/** Text đã cắt khoảng trắng; thiếu/rỗng → dấu trống trung thực "—", tuyệt đối không bịa. */
function textOrDash(value: string | null | undefined): string {
  const trimmed = value?.trim() ?? "";
  return trimmed === "" ? "—" : trimmed;
}

type CemeteryTone = "success" | "warning" | "neutral";

/**
 * Từ vựng trạng thái nghĩa trang — trùng bảng nhãn SCR-032 (Task 5 giữ nó ở phạm vi
 * module của `CemeteryPage.tsx`; màn này không được sửa file danh sách nên khai báo
 * lại nguyên văn). Mã lạ LUÔN hiển thị nguyên văn, tone neutral.
 */
const CEMETERY_STATUS_LABELS: Readonly<
  Record<string, Readonly<{ label: string; tone: CemeteryTone }>>
> = {
  ACTIVE: { label: "Đang quản lý", tone: "success" },
  CLOSED: { label: "Đã đóng", tone: "warning" },
  MERGED: { label: "Đã sáp nhập", tone: "neutral" },
};

/**
 * Từ vựng thay đổi quản lý — khóa wire theo Spec S11.3 §4.1 / ManagementChangeType.
 * Humanise toàn bộ token kỹ thuật sang tiếng Việt nếu backend không cung cấp displayName.
 */
const CHANGE_TYPE_LABELS: Readonly<Record<string, string>> = {
  INITIAL: "Khởi tạo",
  MANAGEMENT_CHANGE: "Đổi đơn vị quản lý, địa bàn",
  CLOSE: "Đóng nghĩa trang",
  REOPEN: "Mở lại nghĩa trang",
  MERGE: "Sáp nhập nghĩa trang",
};

function cemeteryStatusLabel(code: string, displayName?: unknown): string {
  if (typeof displayName === "string" && displayName.trim().length > 0) {
    return displayName.trim();
  }
  return CEMETERY_STATUS_LABELS[code]?.label ?? label(code, displayName);
}

function cemeteryStatusTone(code: string): CemeteryTone {
  return CEMETERY_STATUS_LABELS[code]?.tone ?? "neutral";
}

function changeTypeLabel(code: string, displayName?: unknown): string {
  if (typeof displayName === "string" && displayName.trim().length > 0) {
    return displayName.trim();
  }
  return CHANGE_TYPE_LABELS[code] ?? label(code, displayName);
}

/**
 * Giá trị form cập nhật — chỉ chứa field `UpdateCemeteryRequest` cho phép. Mọi field
 * đều optional vì rc-field-form chỉ trả các field ĐÃ ĐĂNG KÝ (nhóm "đổi đơn vị quản lý"
 * không đăng ký cho tới khi người dùng mở nhóm), nên `onFinish` phải đọc phòng thủ.
 */
type UpdateFormValues = Readonly<{
  name?: string;
  address?: string;
  areaM2?: number | null;
  constructionYear?: number | null;
  publicDescription?: string;
  status?: string;
  managingOrganizationId?: string;
  adminAreaId?: string;
  effectiveFrom?: Dayjs | null;
  decisionReference?: string;
}>;

function initialValuesOf(cemetery: CemeteryData): UpdateFormValues {
  return {
    name: cemetery.name,
    address: cemetery.address ?? "",
    areaM2: cemetery.areaM2 ?? null,
    constructionYear: cemetery.constructionYear ?? null,
    publicDescription: cemetery.publicDescription ?? "",
    status: cemetery.status,
    managingOrganizationId: "",
    adminAreaId: "",
    effectiveFrom: null,
    decisionReference: "",
  };
}

function StatTile({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div className="s11-stat">
      <dt className="s11-stat-label">{label}</dt>
      <dd className="s11-stat-value">{value}</dd>
    </div>
  );
}

/** Ô nhãn/giá trị của rail — cùng một nhịp dọc, giá trị căn phải theo cột. */
function RailRow({ label, value, wide }: {
  readonly label: string;
  readonly value: string;
  readonly wide?: boolean;
}) {
  return (
    <div className={wide === true ? "s11-rail-row s11-rail-row-wide" : "s11-rail-row"}>
      <dt className="s11-rail-label">{label}</dt>
      <dd className="s11-rail-value">{value}</dd>
    </div>
  );
}

/**
 * Card "Thông tin quản lý" (rail). Địa chỉ KHÔNG nằm ở đây — nó thuộc dòng meta của header
 * band, nên rail không lặp lại thông tin đã có ngay trên đầu màn.
 */
function ManagementCard({ cemetery }: { readonly cemetery: CemeteryData }) {
  return (
    <section className="s11-detail-panel" aria-labelledby="s11-cemetery-management-heading">
      <h2 id="s11-cemetery-management-heading">Thông tin quản lý</h2>
      <dl className="s11-rail-list">
        <RailRow label="Địa bàn" value={textOrDash(cemetery.administrativeAreaName)} />
        <RailRow label="Đơn vị quản lý" value={textOrDash(cemetery.managingOrganizationName)} />
        <RailRow
          label="Diện tích"
          value={
            cemetery.areaM2 === null || cemetery.areaM2 === undefined
              ? "—"
              : `${formatCount(cemetery.areaM2)} m²`
          }
        />
        <RailRow
          label="Thành lập"
          value={
            cemetery.constructionYear === null || cemetery.constructionYear === undefined
              ? "—"
              : String(cemetery.constructionYear)
          }
        />
        <RailRow label="Mô tả công khai" value={textOrDash(cemetery.publicDescription)} wide />
      </dl>
    </section>
  );
}

/**
 * Card "Thống kê mộ" (rail): hai số tổng + catalog tình trạng vật lý. Mọi giá trị là TEXT
 * trong `<dl>/<dt>/<dd>` — không có input, spinbutton hay control chỉnh tay nào (acceptance
 * §8 "thống kê không thành input"). Danh tính không lặp ở đây: dải danh tính dưới header là
 * nơi duy nhất trình bày phân bố danh tính.
 *
 * `zoneCount` là số nút GỐC của cây phân cấp (suy ra từ `sections`), KHÔNG dùng
 * `sectionCount` của contract: trường đó đếm mọi nút khu+lô+hàng nên dán nhãn "Số khu" sẽ
 * sai (seed demo có 88 nút nhưng chỉ 4 khu) — con số trên màn phải khớp thứ sơ đồ đang vẽ.
 *
 * `conditionCounts` là map MỞ: catalog tình trạng vật lý do domain owner khóa riêng
 * (CR-S11-3-001 D1) nên UI KHÔNG tự phát minh danh mục — khoá hiển thị nguyên văn, sắc thái
 * trung tính (đúng quy tắc "khoá lạ → nguyên văn").
 */
function StatisticsCard({ cemetery }: {
  readonly cemetery: CemeteryData;
}) {
  const stats = cemetery.graveStats;
  const conditions = stats === undefined ? [] : Object.entries(stats.conditionCounts);

  return (
    <section className="s11-detail-panel" aria-labelledby="s11-cemetery-stats-heading">
      <h2 id="s11-cemetery-stats-heading">Thống kê mộ</h2>

      <h3 className="s11-stat-group-heading">Năng lực tiếp nhận</h3>
      <dl className="s11-stat-row">
        <StatTile label="Tổng vị trí quy hoạch" value={formatCount(stats?.totalPositions ?? 0)} />
        <StatTile label="Đang an táng" value={formatCount(cemetery.graveCounts)} />
      </dl>
      <dl className="s11-stat-row">
        <div className="s11-stat s11-stat-vacant">
          <dt className="s11-stat-label">Vị trí trống</dt>
          <dd className="s11-stat-value">{formatCount(stats?.vacantPositions ?? 0)}</dd>
        </div>
        <StatTile label="Đã cất bốc / di chuyển" value={formatCount(stats?.disintermentCount ?? 0)} />
      </dl>

      {conditions.length > 0 ? (
        <>
          <h3 className="s11-stat-group-heading">Theo tình trạng vật lý</h3>
          <dl className="s11-rail-list">
            {conditions.map(([code, count]) => (
              <RailRow key={`condition-${code}`} label={code} value={formatCount(count)} />
            ))}
          </dl>
        </>
      ) : null}
    </section>
  );
}

/** Cột lịch sử quản lý — TUYỆT ĐỐI không có `versionNo` (số phiên bản kỹ thuật). */
const HISTORY_COLUMNS: ColumnsType<CemeteryManagementHistoryEntryData> = [
  {
    title: "Thay đổi",
    dataIndex: "changeType",
    key: "changeType",
    render: (value: string, row: CemeteryManagementHistoryEntryData) =>
      changeTypeLabel(value, (row as Record<string, unknown>).changeTypeDisplayName),
  },
  {
    title: "Đơn vị quản lý",
    dataIndex: "managingOrganizationName",
    key: "managingOrganizationName",
    render: (value: string | null | undefined) => textOrDash(value),
  },
  {
    title: "Địa bàn",
    dataIndex: "administrativeAreaName",
    key: "administrativeAreaName",
    render: (value: string | null | undefined) => textOrDash(value),
  },
  {
    title: "Hiệu lực từ",
    dataIndex: "validFrom",
    key: "validFrom",
    render: (value: string) => formatDateVn(value),
  },
  {
    title: "Hiệu lực đến",
    dataIndex: "validTo",
    key: "validTo",
    render: (value: string | null | undefined) =>
      value === null || value === undefined || value === "" ? "Đang hiệu lực" : formatDateVn(value),
  },
  {
    title: "Số quyết định",
    dataIndex: "decisionReference",
    key: "decisionReference",
    render: (value: string | null | undefined) => textOrDash(value),
  },
  {
    title: "Lý do",
    dataIndex: "reason",
    key: "reason",
    render: (value: string | null | undefined) => textOrDash(value),
  },
  {
    title: "Ghi nhận lúc",
    dataIndex: "recordedAt",
    key: "recordedAt",
    render: (value: string | null | undefined) => formatDateTimeVn(value),
  },
];

/**
 * Pane lịch sử quản lý: CHỈ mount khi tab được mở (rc-tabs lazy) nên không có request
 * nào trước đó. Adapter không trả `meta` (envelope bị `apiRequest` unwrap), vì vậy
 * phân trang dùng nút trước/sau + số trang hiện tại thay vì bịa `total`.
 */
function ManagementHistoryPane({ cemeteryId }: { readonly cemeteryId: string }) {
  const [page, setPage] = useState(0);
  const { state, refetch } = useApiResource(
    (signal) =>
      getCemeteryManagementHistory(cemeteryId, { page, size: HISTORY_PAGE_SIZE }, signal),
    [cemeteryId, page],
  );

  if (state.status === "loading") {
    return (
      <div role="status" aria-label="Đang tải lịch sử quản lý">
        <LoadingSkeleton rows={4} />
      </div>
    );
  }
  if (state.status === "forbidden") {
    return (
      <ForbiddenState description="Lịch sử quản lý không khả dụng trong phạm vi dữ liệu được cấp." />
    );
  }
  if (state.status === "error") {
    return (
      <ErrorState
        title="Không thể tải lịch sử quản lý"
        description="Kiểm tra kết nối hoặc thử lại sau ít phút."
        {...(state.requestId !== undefined ? { requestId: state.requestId } : {})}
        onRetry={refetch}
      />
    );
  }

  const rows = state.data.rows;
  if (rows.length === 0) {
    return (
      <EmptyState
        title="Chưa có lịch sử quản lý"
        description="Nghĩa trang chưa phát sinh thay đổi đơn vị quản lý hoặc địa bàn."
      />
    );
  }

  return (
    <div className="s11-history-pane">
      {state.status === "stale" ? (
        <StaleState
          {...(state.fetchedAt !== undefined
            ? { lastSyncedAt: formatDateTimeVn(state.fetchedAt) }
            : {})}
          onRefresh={refetch}
        />
      ) : null}
      <div className="s11-table-card">
        <Table
          rowKey={(row) => `${row.changeType}-${row.validFrom}-${row.decisionReference ?? ""}`}
          size="small"
          columns={HISTORY_COLUMNS}
          dataSource={[...rows]}
          pagination={false}
          loading={state.status === "refreshing"}
        />
      </div>
      <nav className="s11-history-pager" aria-label="Phân trang lịch sử quản lý">
        <Typography.Text type="secondary">{`Trang ${page + 1}`}</Typography.Text>
        <Space size="small">
          <Button size="small" disabled={page === 0} onClick={() => { setPage((current) => Math.max(0, current - 1)); }}>
            Trang trước
          </Button>
          <Button
            size="small"
            disabled={rows.length < HISTORY_PAGE_SIZE}
            onClick={() => { setPage((current) => current + 1); }}
          >
            Trang sau
          </Button>
        </Space>
      </nav>
    </div>
  );
}

/**
 * Panel cho nhóm thông tin CHƯA CÓ OWNER. Render thông điệp trung thực — không bao giờ
 * dựng bản đồ giả, geometry giả hay ảnh placeholder (Spec §5 SCR-033).
 */
function UnavailablePanel({ title, description }: {
  readonly title: string;
  readonly description: string;
}) {
  return <EmptyState title={title} description={description} />;
}

/** Lỗi nghiệp vụ của lệnh cập nhật — không lộ stack/mã kỹ thuật (Spec §7 S11.6). */
function updateFailureMessage(error: unknown): Readonly<{ message: string; requestId?: string }> {
  const status = error instanceof ApiProblem ? error.status : undefined;
  const requestId = error instanceof ApiProblem ? error.problem.requestId : undefined;
  const message =
    status === 403
      ? "Bạn không có quyền cập nhật nghĩa trang trong phạm vi hiện tại."
      : status === 409
        ? "Hồ sơ đã được thay đổi ở nơi khác. Tải lại rồi thực hiện lại thao tác."
        : status === 404
          ? "Không tìm thấy nghĩa trang cần cập nhật."
          : "Không thể cập nhật hồ sơ nghĩa trang. Vui lòng thử lại.";
  return { message, ...(requestId !== undefined ? { requestId } : {}) };
}

/**
 * Form cập nhật nghĩa trang. CHỈ mở các field `UpdateCemeteryRequest` cho phép:
 * `name`, `address`, `areaM2`, `constructionYear`, `publicDescription`, `status`, và
 * nhóm "Thay đổi đơn vị quản lý" gồm `managingOrganizationId` + `adminAreaId` +
 * `effectiveFrom` + `decisionReference` đi CÙNG NHAU.
 *
 * `expectedVersion` gửi ngầm từ bản ghi đã tải (`cemetery.version`) và KHÔNG bao giờ
 * hiển thị, không nằm trong Form và không có hidden input — optimistic-lock metadata
 * thuộc nhóm NEVER_USER_UI (guardrail §5.3).
 */
function UpdateCemeteryModal({ cemeteryId, cemetery, open, onClose, onUpdated }: {
  readonly cemeteryId: string;
  readonly cemetery: CemeteryData;
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onUpdated: () => void;
}) {
  const { options: statusOptions } = useCatalog("CEMETERY_STATUS");
  const [form] = Form.useForm<UpdateFormValues>();
  const [transfer, setTransfer] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState<string>(() => createRequestId());
  const [submitting, setSubmitting] = useState(false);
  // Phiên form: đổi khoá này khi mở lại để form nạp lại `initialValues` từ bản ghi mới
  // nhất (bản ghi có thể đã đổi sau lần cập nhật trước).
  const [formSession, setFormSession] = useState(0);
  const [failure, setFailure] = useState<
    Readonly<{ message: string; requestId?: string }> | null
  >(null);

  useEffect(() => {
    if (!open) return;
    setTransfer(false);
    setFailure(null);
    setSubmitting(false);
    // Khoá idempotency sinh MỘT lần khi mở form và giữ nguyên qua các lần thử lại —
    // retry không tạo thêm thay đổi (khuôn AdminWorkspace).
    setIdempotencyKey(createRequestId());
    setFormSession((current) => current + 1);
  }, [open]);

  const onFinish = (values: UpdateFormValues) => {
    const trimmedName = (values.name ?? "").trim();
    const address = (values.address ?? "").trim();
    const publicDescription = (values.publicDescription ?? "").trim();
    const status = values.status ?? "";
    const managingOrganizationId = (values.managingOrganizationId ?? "").trim();
    const adminAreaId = (values.adminAreaId ?? "").trim();
    const decisionReference = (values.decisionReference ?? "").trim();
    const effectiveFrom = values.effectiveFrom ?? null;
    const areaM2 = values.areaM2 ?? null;
    const constructionYear = values.constructionYear ?? null;

    const input: UpdateCemeteryInput = {
      // Bắt buộc theo contract; lấy từ bản ghi đang xem, không phải từ người dùng.
      expectedVersion: cemetery.version,
      ...(trimmedName !== "" ? { name: trimmedName } : {}),
      ...(address !== "" ? { address } : {}),
      ...(areaM2 !== null ? { areaM2 } : {}),
      ...(constructionYear !== null ? { constructionYear } : {}),
      ...(publicDescription !== "" ? { publicDescription } : {}),
      ...(status !== "" ? { status } : {}),
      ...(transfer && managingOrganizationId !== "" ? { managingOrganizationId } : {}),
      ...(transfer && adminAreaId !== "" ? { adminAreaId } : {}),
      ...(transfer && effectiveFrom !== null
        ? { effectiveFrom: effectiveFrom.format("YYYY-MM-DD") }
        : {}),
      ...(transfer && decisionReference !== "" ? { decisionReference } : {}),
    };

    setSubmitting(true);
    setFailure(null);
    void updateCemetery(cemeteryId, input, idempotencyKey).then(
      () => {
        setSubmitting(false);
        onUpdated();
      },
      (error: unknown) => {
        setSubmitting(false);
        setFailure(updateFailureMessage(error));
      },
    );
  };

  const requiredWhenTransferring = transfer
    ? [{ required: true, message: "Bắt buộc khi thay đổi đơn vị quản lý" }]
    : [];

  return (
    <Modal
      open={open}
      title="Cập nhật hồ sơ nghĩa trang"
      onCancel={onClose}
      onOk={() => { form.submit(); }}
      okText="Lưu thay đổi"
      cancelText="Hủy"
      confirmLoading={submitting}
      width={720}
    >
      {failure ? (
        <Alert
          type={failure.message.startsWith("Bạn không có quyền") ? "warning" : "error"}
          showIcon
          message={failure.message}
          description={failure.requestId ? `Mã hỗ trợ: ${failure.requestId}` : undefined}
          style={{ marginBottom: "var(--ncc-spacing-md)" }}
        />
      ) : null}
      <Form
        key={formSession}
        form={form}
        layout="vertical"
        initialValues={initialValuesOf(cemetery)}
        onFinish={onFinish}
      >
        <Form.Item
          label="Tên nghĩa trang"
          name="name"
          rules={[{ required: true, message: "Nhập tên nghĩa trang" }]}
        >
          <Input />
        </Form.Item>
        <Form.Item label="Địa chỉ" name="address">
          <Input />
        </Form.Item>
        <Space size="middle" wrap style={{ display: "flex", alignItems: "flex-start" }}>
          <Form.Item label="Diện tích (m²)" name="areaM2">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item label="Năm thành lập" name="constructionYear">
            <InputNumber min={1800} max={2100} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item label="Trạng thái" name="status">
            <Select options={[...statusOptions]} style={{ width: "100%" }} />
          </Form.Item>
        </Space>
        <Form.Item label="Mô tả công khai" name="publicDescription">
          <Input.TextArea rows={3} />
        </Form.Item>

        <fieldset className="s11-form-group">
          <legend>Thay đổi đơn vị quản lý</legend>
          <Typography.Paragraph type="secondary" className="s11-detail-hint">
            Đổi đơn vị quản lý hoặc địa bàn ghi thêm một dòng lịch sử hiệu lực, nên bốn
            thông tin dưới đây phải đi cùng nhau.
          </Typography.Paragraph>
          <Checkbox checked={transfer} onChange={(event) => { setTransfer(event.target.checked); }}>
            Đổi đơn vị quản lý hoặc địa bàn
          </Checkbox>
          {transfer ? (
            <>
              <Form.Item
                label="Mã đơn vị quản lý"
                name="managingOrganizationId"
                rules={requiredWhenTransferring}
              >
                <Input />
              </Form.Item>
              <Form.Item label="Mã địa bàn" name="adminAreaId" rules={requiredWhenTransferring}>
                <Input />
              </Form.Item>
              <Form.Item label="Hiệu lực từ" name="effectiveFrom" rules={requiredWhenTransferring}>
                <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item
                label="Số quyết định"
                name="decisionReference"
                rules={requiredWhenTransferring}
              >
                <Input />
              </Form.Item>
            </>
          ) : null}
        </fieldset>
      </Form>
    </Modal>
  );
}

export function CemeteryDetailPage() {
  const { cemeteryId = "" } = useParams<{ cemeteryId: string }>();
  const [notFound, setNotFound] = useState(false);
  const [updateOpen, setUpdateOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  // Tab đang mở do trang giữ (không phải Tabs tự giữ) để sau này còn điều khiển được tab nào
  // đang mở bằng code. KHÔNG dùng cho nút "Quản trị Sơ đồ mộ & GIS" ở header: nút đó mở TRANG
  // RIÊNG `/cemeteries/:cemeteryId/gis` — bề mặt LÀM VIỆC (sửa toạ độ, nhập GeoJSON).
  // Bản trước của nút chỉ gọi `setActiveTab("map")` với lý do ghi ở đây là "route /gis nay đã bỏ";
  // route đó CHƯA TỪNG bị bỏ (`App.tsx:71` vẫn còn) — nên nút đã mở nhầm đúng cái tab CHỈ-ĐỌC
  // nằm ngay bên dưới nó, trong khi nhãn hứa một bề mặt quản trị. Sửa 17/09/2026.
  const [activeTab, setActiveTab] = useState("documents");

  const { state, refetch } = useApiResource(
    async (signal) => {
      try {
        const data = await getCemetery(cemeteryId, signal);
        setNotFound(false);
        return data;
      } catch (error: unknown) {
        // 404 là "không tìm thấy" chứ không phải lỗi hệ thống — trạng thái riêng.
        setNotFound(error instanceof ApiProblem && error.status === 404);
        throw error;
      }
    },
    [cemeteryId],
  );

  const actorState = useActorContext();
  const actor: CurrentUser | null =
    actorState.status === "ready"
    || actorState.status === "refreshing"
    || actorState.status === "stale"
      ? actorState.actor
      : null;

  const cemetery =
    state.status === "ready" || state.status === "refreshing" || state.status === "stale"
      ? state.data
      : null;

  // Hành động hiếm: chỉ render khi grant có DATA_WRITE ĐÚNG scope bản ghi. Backend vẫn
  // là nơi cưỡng chế quyền — đây thuần tuý là quyết định hiển thị.
  const canUpdate = actor !== null && can(actor, "DATA_WRITE", { type: "CEMETERY", id: cemeteryId });

  const address = cemetery?.address?.trim() ?? "";

  return (
    <div className="s11-detail-frame">
      <Typography.Paragraph style={{ marginBottom: 0 }}>
        <Link to="/cemeteries">← Danh sách nghĩa trang</Link>
      </Typography.Paragraph>

      <PageHeading
        title={cemetery?.name ?? "Hồ sơ nghĩa trang"}
        description={
          cemetery ? (
            <span className="s11-header-meta">
              <span>{cemetery.cemeteryCode}</span>
              <span className="s11-meta-sep" aria-hidden="true">
                ·
              </span>
              <StatusBadge
                tone={cemeteryStatusTone(cemetery.status)}
                label={cemeteryStatusLabel(cemetery.status, (cemetery as Record<string, unknown>).statusDisplayName)}
              />
              {address === "" ? null : (
                <>
                  <span className="s11-meta-sep" aria-hidden="true">
                    ·
                  </span>
                  <span>{address}</span>
                </>
              )}
            </span>
          ) : (
            "Thông tin quản lý, thống kê mộ và phân cấp khu — lô — hàng trong phạm vi dữ liệu được cấp."
          )
        }
        action={
          <Space>
            {cemetery ? (
              <Link to={`/cemeteries/${encodeURIComponent(cemeteryId)}/gis`}>
                <Button type="primary" icon={<CompassOutlined />}>
                  Quản trị Sơ đồ mộ & GIS
                </Button>
              </Link>
            ) : null}
            {/* CR-PUB3D-001 / DEC-100 D4 — chỉ hiện khi nghĩa trang THẬT SỰ có giao diện công khai
                (`publicSlug` do backend tra allowlist). Hai ràng buộc cố ý, đừng "sửa cho đẹp":
                1. `<a>` THẬT (Button href), KHÔNG `<Link>`. `/public/**` thuộc `PublicApp` — một gốc
                   router khác, chọn bằng pathname ở `main.tsx`. `<Link>` trong `App` không khớp
                   route nào nên rơi vào catch-all `*` → `Navigate to="/martyrs"` (App.tsx) và
                   điều hướng về dashboard trong IM LẶNG. Phải để trình duyệt tải lại trang.
                2. Tab MỚI. Mở cùng tab là rời khu quản trị; bấm Back quay lại `/cemeteries/:id`
                   bằng lượt tải trang đầy đủ, mà app không giữ phiên OIDC qua lượt tải đầy đủ
                   (`redirect_uri` là gốc) ⇒ bị đá qua Keycloak rồi về `/`, mất deep link. */}
            {cemetery?.publicSlug ? (
              <Button
                icon={<CompassOutlined />}
                href={`/public/cemeteries/${encodeURIComponent(cemetery.publicSlug)}/3d`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Xem giao diện 3D
              </Button>
            ) : null}
            {canUpdate ? (
              <Button
                onClick={() => {
                  setNotice(null);
                  setUpdateOpen(true);
                }}
              >
                Cập nhật hồ sơ
              </Button>
            ) : null}
            <Button
              icon={<ReloadOutlined />}
              aria-label="Làm mới"
              loading={state.status === "refreshing"}
              onClick={refetch}
            >
              Làm mới
            </Button>
          </Space>
        }
      />

      {notice ? (
        <Alert
          type="success"
          showIcon
          message={notice}
          className="s11-detail-notice"
        />
      ) : null}

      {state.status === "stale" ? (
        <div className="s11-detail-zone">
          <StaleState
            {...(state.fetchedAt !== undefined
              ? { lastSyncedAt: formatDateTimeVn(state.fetchedAt) }
              : {})}
            onRefresh={refetch}
          />
        </div>
      ) : null}

      {state.status === "loading" ? (
        <div className="s11-detail-zone" role="status" aria-label="Đang tải hồ sơ nghĩa trang">
          <LoadingSkeleton rows={6} />
        </div>
      ) : null}

      {state.status === "forbidden" ? (
        <div className="s11-detail-zone">
          <ForbiddenState description="Hồ sơ nghĩa trang không khả dụng trong phạm vi dữ liệu được cấp." />
        </div>
      ) : null}

      {state.status === "error" && notFound ? (
        <div className="s11-detail-zone">
          <EmptyState
            title="Không tìm thấy nghĩa trang"
            description="Hồ sơ có thể đã sáp nhập hoặc không còn trong phạm vi tra cứu."
            action={<Link to="/cemeteries">Về danh sách nghĩa trang</Link>}
          />
        </div>
      ) : null}

      {state.status === "error" && !notFound ? (
        <div className="s11-detail-zone">
          <ErrorState
            title="Không thể tải hồ sơ nghĩa trang"
            description="Kiểm tra kết nối hoặc thử lại sau ít phút."
            {...(state.requestId !== undefined ? { requestId: state.requestId } : {})}
            onRetry={refetch}
          />
        </div>
      ) : null}

      {cemetery ? (
        <>
          <IdentityStrip stats={cemetery.graveStats} />

          <div className="s11-detail-grid">
            <div className="s11-detail-main">
              {/* Khối "Phân cấp khu — lô — hàng" và trang sơ đồ mộ của khu đã bị XOÁ: chúng chỉ
                  đếm được NÚT trong cây `sections`, không biết số mộ. Bản đồ trong tab "Bản đồ"
                  làm được nhiều hơn — mỗi ô mộ mang `sectionId` nên đếm được số mộ theo khu, và
                  bấm ô thì sang thẳng hồ sơ mộ. */}
              <section className="s11-detail-panel" aria-label="Thông tin bổ trợ hồ sơ">
                <Tabs
                  className="s11-detail-tabs"
                  activeKey={activeTab}
                  onChange={setActiveTab}
                  items={[
                    {
                      key: "documents",
                      label: "Tài liệu và ảnh",
                      children: (
                        <UnavailablePanel
                          title="Chưa có tài liệu và ảnh cho nghĩa trang này"
                          description="Tính năng tài liệu và ảnh của nghĩa trang chưa khả dụng trong phiên bản hiện tại."
                        />
                      ),
                    },
                    {
                      key: "map",
                      label: "Bản đồ",
                      children: <GisMapPage embedded cemeteryId={cemeteryId} />,
                    },
                    {
                      key: "history",
                      label: "Lịch sử quản lý",
                      children: <ManagementHistoryPane cemeteryId={cemeteryId} />,
                    },
                  ]}
                />
              </section>
            </div>

            <aside className="s11-rail" aria-label="Thông tin hồ sơ">
              <ManagementCard cemetery={cemetery} />
              <StatisticsCard cemetery={cemetery} />
            </aside>
          </div>

          {canUpdate ? (
            <UpdateCemeteryModal
              cemeteryId={cemeteryId}
              cemetery={cemetery}
              open={updateOpen}
              onClose={() => { setUpdateOpen(false); }}
              onUpdated={() => {
                setUpdateOpen(false);
                setNotice("Đã gửi thay đổi hồ sơ nghĩa trang. Dữ liệu đang được tải lại.");
                refetch();
              }}
            />
          ) : null}
        </>
      ) : null}
    </div>
  );
}
